import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../contexts/AuthContext';
import { LOGIN_MUTATION } from '../../graphql/mutations';
import { PLATFORM_SETTINGS_QUERY } from '../../graphql/queries';
import BrandMark from '../../components/common/BrandMark';
import styles from './Login.module.css';
import Icon from '../../components/common/Icon';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // In-app paths only — a full URL here would be an open redirect.
  const redirectParam = searchParams.get('redirect');
  const redirectTo = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : '/dashboard';

  const { data: settingsData } = useQuery(PLATFORM_SETTINGS_QUERY, { fetchPolicy: 'cache-first' });
  const registrationEnabled = settingsData?.platformSettings?.usersAllowedToRegister ?? true;

  const [loginMutation, { loading }] = useMutation(LOGIN_MUTATION, {
    onCompleted: (data) => {
      login(data.login.token, data.login.user);
      navigate(redirectTo);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    loginMutation({ variables: { email, password } });
  };

  return (
    <div className={styles.container}>
      <div className={styles.background}>
        <div className={styles.bgGradient} />
        <div className="absolute top-20 right-10 hidden lg:block z-0 opacity-20 transform rotate-12">
          <Icon name="grid_4x4" className="text-primary" style={{ fontSize: '120px', strokeWidth: 0.6 }} />
        </div>
      </div>

      <main className={styles.main}>
        <div className={styles.logoSection}>
          <div className={styles.logoIcon}>
            <BrandMark size={52} decorative />
          </div>
          <h1 className="font-headline text-3xl font-bold tracking-tight text-on-surface">Infrio</h1>
          <p className="text-on-surface-variant text-sm mt-1">Geospatial Intelligence Secured</p>
        </div>

        <div className={styles.card}>
          <div className="mb-8 text-center md:text-left">
            <h2 className="text-xl font-headline font-semibold text-on-surface">Welcome back</h2>
            <p className="text-on-surface-variant text-sm">Please enter your credentials to access your projects.</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-base" />
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant font-label" htmlFor="email">
                Work Email
              </label>
              <div className={styles.inputWrapper}>
                <Icon name="alternate_email" className={`text-lg ${styles.inputIcon}`} />
                <input
                  className={styles.input}
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@company.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant font-label" htmlFor="password">
                  Password
                </label>
                <Link
                  className="text-xs font-medium text-primary hover:underline"
                  to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}
                >
                  Forgot?
                </Link>
              </div>
              <div className={styles.inputWrapper}>
                <Icon name="lock" className={`text-lg ${styles.inputIcon}`} />
                <input
                  className={styles.input}
                  style={{ paddingRight: '3rem' }}
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'visibility_off' : 'visibility'} className="text-lg" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 btn-primary rounded-lg shadow-md shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Icon name="refresh" className="text-lg animate-spin" />
                  Logging in...
                </>
              ) : (
                <>
                  <span>Log In</span>
                  <Icon name="arrow_forward" className="text-lg" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8">
            <div className="relative flex items-center justify-center mb-6">
              <div className="flex-grow border-t border-outline-variant/30"></div>
              <span className="mx-4 text-xs font-label text-outline uppercase tracking-widest">Or secure connect with</span>
              <div className="flex-grow border-t border-outline-variant/30"></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button className="flex items-center justify-center gap-2 py-2.5 px-4 bg-surface-container-low border border-outline-variant/10 rounded-lg hover:bg-surface-container-highest transition-colors font-medium text-sm text-on-surface">
                <Icon name="account_circle" className="text-lg" />
                Google
              </button>
              <button className="flex items-center justify-center gap-2 py-2.5 px-4 bg-surface-container-low border border-outline-variant/10 rounded-lg hover:bg-surface-container-highest transition-colors font-medium text-sm text-on-surface">
                <Icon name="hub" className="text-lg" />
                SSO
              </button>
            </div>
          </div>

          {registrationEnabled && (
            <div className="mt-8 text-center">
              <p className="text-sm text-on-surface-variant">
                New to Infrio?{' '}
                <Link to="/register" className="text-primary font-semibold hover:underline decoration-2 underline-offset-4 ml-1">
                  Create an account
                </Link>
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-6 opacity-40">
            {([
              { icon: 'encrypted', label: 'AES-256 Encrypted' },
              { icon: 'vpn_lock', label: 'GDPR Compliant' },
            ] as const).map((badge) => (
              <div key={badge.label} className="flex items-center gap-2">
                <Icon name={badge.icon} className="text-xs" />
                <span className="text-[10px] font-bold tracking-tighter uppercase font-label">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
